/**
 * `expected_rev` for the collections surface — and it is a CONTENT HASH, not a
 * stored revision, because this surface has nowhere to keep one.
 *
 * ## Why this module exists at all
 *
 * Every other MCP write surface gets `rev` for free. `extension_documents` has
 * a real `rev` TEXT column and `records.rs::cas_upsert_in` compares it inside
 * the transaction, so `board_update` and `appointment_update` hand Rust an
 * `expectedRev` and Rust refuses a stale write atomically.
 *
 * **Collections have none of that.** `sync/schema.rs:29-53` gives `collections`
 * and `requests` an integer `version` and an `updated_at`, and `load_family`
 * (`sync/store.rs:547-565`) selects `data` alone — so neither number ever
 * crosses to JS. There is no CAS command, no `_list_meta`, and the only write
 * primitive is a whole-workspace snapshot save. PLAN §D1 says this in as many
 * words and calls for the guarantee to be rebuilt on top of what does exist.
 *
 * So `rev` here is `hash(canonicalJson(record))`, computed by ONE function that
 * both the read projection and the writer call. The wire word stays
 * `expected_rev` on purpose (**align, don't invent**): the whole existing
 * vocabulary — the `conflict` and `expected_rev_required` codes, the
 * `INSTRUCTIONS` sentence "pass back the rev you read" — then applies here
 * unchanged, and a client that already speaks it needs no second mechanism.
 *
 * ## What this rev DOES and DOES NOT promise. Read this before trusting it.
 *
 * * It DOES detect that the record changed between the read and the write.
 *   That is the whole job: it turns "the agent overwrites an edit the user made
 *   thirty seconds ago" into a `conflict` the agent must re-read to resolve.
 * * It is NOT a compare-and-swap. The check happens in JS, inside the
 *   module-level mutex in `collectionsDb.js`, which serialises MCP against MCP
 *   but says nothing about MCP against the USER's own typing. The window is one
 *   React commit and it is the same window every UI action already has. Stated
 *   as a cost in `IMPLEMENTATION.md`, not papered over.
 * * It is NOT a security control and nothing may ever treat it as one. Anyone
 *   able to forge a rev already holds write access to the store.
 *
 * ## Why not SHA-256
 *
 * `crypto.subtle.digest` is asynchronous and absent from jsdom, and the only
 * other SHA-256 in this repo (`utils/awsSigV4.js:15`) is `async` for exactly
 * that reason. A rev must be computable synchronously inside a zustand-store
 * read and for every request of a collection in one `collection_read`, so an
 * async digest would either make the read async all the way up or force a
 * BigInt implementation whose per-byte cost over a 32 KB record, times a
 * hundred requests, is seconds.
 *
 * What is here instead is two independent 32-bit hashes with different seeds
 * and mixers, each finalised through murmur3's `fmix32` avalanche, rendered as
 * 16 lowercase hex characters. 64 bits of change detection, `Math.imul`-fast,
 * deterministic across devices. For the job described above that is the right
 * tool; for anything that needs collision RESISTANCE it is not, and nothing
 * here asks it to be.
 */

/**
 * Deterministic JSON: object keys sorted, `undefined` dropped, arrays in
 * order. Two records that differ only in key insertion order MUST hash the
 * same, or a rev would go stale on a re-serialisation that changed nothing —
 * and every collections record is round-tripped through the whole-workspace
 * snapshot, which is exactly where key order moves.
 */
export function canonicalJson(value) {
  if (value === null) return 'null';
  const t = typeof value;
  if (t === 'number') return Number.isFinite(value) ? String(value) : 'null';
  if (t === 'boolean') return value ? 'true' : 'false';
  if (t === 'string') return JSON.stringify(value);
  if (t === 'undefined' || t === 'function') return 'null';
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const keys = Object.keys(value)
    .filter((k) => value[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(value[k])}`).join(',')}}`;
}

/** murmur3's finaliser: the avalanche step, so one changed byte moves every bit. */
function fmix32(input) {
  let h = input | 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

const hex8 = (n) => (n >>> 0).toString(16).padStart(8, '0');

/** 64 bits as 16 lowercase hex, from two independent 32-bit passes. */
export function contentHash(text) {
  const s = String(text);
  // FNV-1a, and a second pass with a different seed, a different multiplier
  // and the byte's INDEX mixed in — so the two halves cannot move together on
  // a transposition, which a single FNV pass is weak against.
  let a = 0x811c9dc5 | 0;
  let b = 0x9e3779b9 | 0;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193);
    b = Math.imul(b ^ (c + i), 0x85ebca6b);
    b = (b << 13) | (b >>> 19);
  }
  // The length is mixed in as well, so `["a","bc"]` and `["ab","c"]` cannot
  // collide on the character stream alone.
  a = Math.imul(a ^ s.length, 0x01000193);
  return hex8(fmix32(a)) + hex8(fmix32(b));
}

/** The shape a client must send back. 16 lowercase hex, nothing else. */
export const REV_RE = /^[0-9a-f]{16}$/;

/**
 * A REQUEST's rev — over the whole record.
 *
 * A request is one row in the `requests` table and its `data` is the whole
 * object, so everything in it is in scope: url, headers, body, auth, mock
 * examples, both scripts. That is deliberate even for the fields MCP cannot
 * see: if a human edits a script this server may not read (**O33**), an MCP
 * update against a rev taken before that edit is a `conflict`, which is the
 * right answer. A rev that covered only the visible fields would let this
 * server overwrite the invisible ones without ever noticing.
 */
export function revOfRequest(request) {
  return contentHash(canonicalJson(request ?? null));
}

/**
 * A COLLECTION's rev — over the collection WITHOUT its requests.
 *
 * This mirrors how the record is actually stored. `split_collections`
 * (`sync/store.rs:307-327`) lifts `requests` into their own table and says so
 * in its own comment: *"everything else on the collection object — including
 * the folder tree in settings — stays in data."* So the collection's `data` is
 * exactly this, and the rev covering exactly this is what makes
 * `collection_update` and `folder_rename` usable: adding a request must not
 * invalidate the rev someone is holding in order to rename a folder, and the
 * two changes genuinely do not conflict.
 */
export function revOfCollection(collection) {
  if (!collection || typeof collection !== 'object') return contentHash('null');
  const { requests, ...rest } = collection;
  return contentHash(canonicalJson(rest));
}
